import React, { useCallback, useContext, useState } from 'react';
import { isPortfolioBlock, MAX_BLOCK_SUBJECT_INPUTS, useHasFF, useHover } from 'venn-utils';
import { useRecoilValue } from 'recoil';
import { blockMaxSubjects, blockSettings, blockSubjectInputGroups } from 'venn-state';
import { FlexHeader } from '../../../../shared';
import { HoverIcon } from './HoverIcon';
import { BlockSubjectInputList } from './BlockSubjectInputList';
import { UserContext } from 'venn-components';
import styled from 'styled-components';
import { noop } from 'lodash';
import { ComparisonOrderSubSection } from './ComparisonOrderSubSection';
import { FactorTrendLineChartMessage } from '../messages/FactorTrendLineChartMessage';
import { NeedsPublicPrivateMessage } from '../messages/NeedsPublicPrivateMessage';
import { ExcludedSubjectsMessage } from '../messages/ExcludedSubjectsMessage';
import { HistoricalProFormaSubjectsMessage } from '../messages/HistoricalProFormaSubjectsMessage';

// TODO(VENN-28896): Refactor or delete this component entirely
/** Section for editing a block config with regard to its {@link blockSubjectInputGroups}: viewing, adding, editing, and deleting subject input groups. */
export const SubjectInputSection = React.memo(function SubjectInputSection({
  selectedBlockId,
}: {
  selectedBlockId: string;
}) {
  const { hasPermission } = useContext(UserContext);
  const hasHoldingsMetadata = useHasFF('holdings_metadata');

  const [isRootHovered, rootHoverHandlers] = useHover();
  const blockSetting = useRecoilValue(blockSettings(selectedBlockId));

  const [isAdding, setIsAdding] = useState(false);
  const setIsAddingTrue = useCallback(() => setIsAdding(true), [setIsAdding]);
  const setIsAddingFalse = useCallback(() => setIsAdding(false), [setIsAdding]);
  const canAddSubjectGroup = hasPermission('STUDIO_EDIT_SUBJECTS') && hasPermission('STUDIO_EDIT');

  return (
    <div {...rootHoverHandlers} data-testid="qa-block-subject-group">
      <FlexHeader style={{ justifyContent: 'space-between', marginBottom: '12px' }}>
        <span>Subjects</span>
        {canAddSubjectGroup && (
          <AddSubjectHoverButton
            selectedBlockId={selectedBlockId}
            forceVisible={isRootHovered}
            onClick={setIsAddingTrue}
          />
        )}
      </FlexHeader>
      <LayoutWrapper>
        <FactorTrendLineChartMessage selectedBlockId={selectedBlockId} />
        <NeedsPublicPrivateMessage selectedBlockId={selectedBlockId} />
        <ExcludedSubjectsMessage selectedBlockId={selectedBlockId} />

        <BlockSubjectInputList
          selectedBlockId={selectedBlockId}
          isAdding={isAdding}
          onAddingFinished={setIsAddingFalse}
        />
        <HistoricalProFormaSubjectsMessage selectedBlockId={selectedBlockId} />
      </LayoutWrapper>
      {selectedBlockId && isPortfolioBlock(blockSetting.customBlockType) && !hasHoldingsMetadata && (
        <ComparisonOrderSubSection selectedBlockId={selectedBlockId} />
      )}
    </div>
  );
});

const LayoutWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// TODO(VENN-24534): add a display name to this React component
// eslint-disable-next-line react/display-name
const AddSubjectHoverButton = React.memo(
  ({
    selectedBlockId,
    forceVisible,
    onClick,
  }: {
    selectedBlockId: string;
    forceVisible?: boolean;
    onClick: () => void;
  }) => {
    const blockInputIds = useRecoilValue(blockSubjectInputGroups(selectedBlockId));
    const maxSubjects = useRecoilValue(blockMaxSubjects(selectedBlockId));

    const haxMaxSubjectGroups = blockInputIds.length >= Math.min(maxSubjects, MAX_BLOCK_SUBJECT_INPUTS);
    const action = 'Add subject group';

    const plusButtonProps = haxMaxSubjectGroups
      ? {
          tooltip:
            maxSubjects < MAX_BLOCK_SUBJECT_INPUTS
              ? `This block supports a max of ${maxSubjects} subjects`
              : `Blocks support up to ${MAX_BLOCK_SUBJECT_INPUTS} subject input groups`,
          disabled: true,
          buttonLabel: action,
          onClick: noop,
        }
      : {
          onClick,
          buttonLabel: action,
          disabled: false,
        };

    return (
      <HoverIcon type="plus" tabIndex={0} visible={forceVisible || blockInputIds.length === 0} {...plusButtonProps} />
    );
  },
);
